<template>
  <div class="yc-logs">
    <div class="page-head">
      <h2>日志</h2>
      <div class="page-actions">
        <el-select v-model="projectFilter" placeholder="全部项目" clearable size="small" style="width: 140px" @change="onFilterChange">
          <el-option label="影传" value="影传" />
          <el-option label="影控" value="影控" />
        </el-select>
        <el-date-picker
          v-model="dateFilter"
          type="date"
          placeholder="按日期"
          size="small"
          value-format="yyyy-MM-dd"
          style="width: 150px"
          @change="onFilterChange"
        />
        <el-button type="primary" size="small" :loading="loading" @click="loadLogs">刷新</el-button>
      </div>
    </div>

    <el-table ref="table" :data="logs" stripe border height="calc(100vh - 230px)" v-loading="loading">
      <el-table-column prop="project_name" label="项目" width="100" align="center">
        <template slot-scope="scope">
          <el-tag size="mini" :type="scope.row.project_name === '影传' ? 'success' : 'warning'">{{ scope.row.project_name }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="upload_date" label="日期" width="130" :formatter="fmtCell" />
      <el-table-column prop="original_name" label="原始文件名" width="200" show-overflow-tooltip />
      <el-table-column prop="ip" label="上报 IP" width="150" />
      <el-table-column prop="log_file_path" label="存盘路径" min-width="280" show-overflow-tooltip />
      <el-table-column label="操作" width="160" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="viewContent(scope.row)">查看内容</el-button>
          <el-button type="text" size="small" @click="viewLog(scope.row)">查看日志</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog :title="contentTitle" :visible.sync="dialogVisible" width="720px" top="8vh">
      <pre class="log-content" v-loading="contentLoading">{{ logContent }}</pre>
    </el-dialog>

    <el-pagination
      v-if="total > pageSize"
      class="pager"
      layout="prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page="page"
      @current-change="onPage"
    />

    <el-alert
      v-if="usingMock"
      class="todo-tip"
      type="warning"
      :closable="false"
      title="当前为 mock 数据（后端 GET /api/client/logs 未就绪）"
      description="已按 API.md 7.3 契约接入：列表返回 content 字段（点「查看内容」）；log 文件原文走 GET /api/client/logs/:id/content（点「查看日志」）。"
    />
  </div>
</template>

<script>
import { getLogs, getLogContent } from '../../api'
import { formatCST } from '../../utils/format'

// 真实接口未就绪时的兜底 mock（字段与 API.md 7.3.1 返回一致，含 content）
const MOCK_LOGS = [
  { id: 1, ip: '1.2.3.4', project_name: '影控', original_name: 'app_8821.log', upload_date: '2026-08-27', log_file_path: '/logs/影控/2026-08-27/8821.txt', created_at: '2026-08-27 15:00:00', content: '[15:00:00] INFO  app start\n[15:00:01] INFO  camera connected: Canon EOS 6D\n[15:00:02] WARN  transfer slow, retry=1\n[15:00:03] INFO  upload ok' },
  { id: 2, ip: '5.6.7.8', project_name: '影传', original_name: 'app_5512.log', upload_date: '2026-08-27', log_file_path: '/logs/影传/2026-08-27/5512.txt', created_at: '2026-08-27 14:30:00', content: '[14:30:00] INFO  app start\n[14:30:01] INFO  discover camera via SSDP\n[14:30:02] ERROR Err12 connection reset\n[14:30:03] INFO  reconnect success' },
  { id: 3, ip: '9.10.11.12', project_name: '影控', original_name: 'app_3390.log', upload_date: '2026-08-26', log_file_path: '/logs/影控/2026-08-26/3390.txt', created_at: '2026-08-26 19:12:00', content: '[19:12:00] INFO  app start\n[19:12:01] INFO  export begin\n[19:12:05] INFO  export done (128 files)' },
  { id: 4, ip: '13.14.15.16', project_name: '影传', original_name: 'app_2048.log', upload_date: '2026-08-25', log_file_path: '/logs/影传/2026-08-25/2048.txt', created_at: '2026-08-25 10:05:00', content: '[10:05:00] INFO  app start\n[10:05:01] INFO  camera connected\n[10:05:02] INFO  download thumbnails x24' },
]

export default {
  name: 'YcLogs',
  data() {
    return {
      loading: false,
      usingMock: false,
      projectFilter: '',
      dateFilter: '',
      page: 1,
      pageSize: 20,
      total: 0,
      logs: [],
      dialogVisible: false,
      contentLoading: false,
      contentTitle: '内容',
      logContent: '',
    }
  },
  methods: {
    fmtCell(row, col, val) {
      return formatCST(val)
    },
    onFilterChange() {
      this.page = 1
      this.loadLogs()
    },
    async loadLogs() {
      this.loading = true
      try {
        const { list, total } = await getLogs({
          project: this.projectFilter,
          date: this.dateFilter,
          page: this.page,
          pageSize: this.pageSize,
        })
        this.logs = list
        this.total = total
        this.usingMock = false
      } catch (e) {
        // 后端 GET 未实现（404）或网络异常 → 回退 mock
        this.logs = this.projectFilter
          ? MOCK_LOGS.filter((l) => l.project_name === this.projectFilter)
          : MOCK_LOGS
        this.total = this.logs.length
        this.usingMock = true
      } finally {
        this.loading = false
      }
    },
    onPage(p) {
      this.page = p
      this.loadLogs()
    },
    // 查看内容：列表里已返回的 content 字段
    viewContent(row) {
      this.contentTitle = '日志内容（content 字段）'
      this.logContent = row.content || '（无内容）'
      this.dialogVisible = true
    },
    // 查看日志：读取 log_file_path 指向的文件原文（GET /api/client/logs/:id/content）
    async viewLog(row) {
      this.contentTitle = '日志文件内容（' + (row.original_name || row.id) + '）'
      this.logContent = ''
      this.dialogVisible = true
      if (this.usingMock) {
        // mock 模式无后端文件，用 content 字段兜底展示
        this.logContent = row.content || '（无内容）'
        return
      }
      this.contentLoading = true
      try {
        this.logContent = await getLogContent(row.id)
      } catch (e) {
        this.logContent = '（读取失败：' + (e && e.message ? e.message : e) + '）'
      } finally {
        this.contentLoading = false
      }
    },
  },
  created() {
    this.loadLogs()
  },
}
</script>

<style scoped>
.yc-logs {
  padding: 20px;
}
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.page-head h2 {
  margin: 0;
}
.page-actions {
  display: flex;
  gap: 8px;
}
.pager {
  margin-top: 14px;
  text-align: right;
}
.log-content {
  max-height: 60vh;
  overflow: auto;
  margin: 0;
  padding: 12px;
  background: #1e1e1e;
  color: #d4d4d4;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
}
.todo-tip {
  margin-top: 16px;
}
</style>



// WEBPACK FOOTER //
// src/admin/views/yingchuan/Logs.vue